import React, { useState } from "react";

import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

import Select from "../generic/Select";
import DatePicker from "../generic/DatePicker";
import AddItems from "./AddItems";
import TimePicker from "./TimePicker";

const priorityOptions = [
  { value: "1", label: "High" },
  { value: "2", label: "Medium" },
  { value: "3", label: "Low" },
];

const categoryOptions = [
  { value: "1", label: "Setup" },
  { value: "2", label: "Testing" },
  { value: "3", label: "Operation" },
  { value: "4", label: "Breakdown" },
  { value: "5", label: "Post-Production" },
];

const TaskForm = ({ passRef, handleSubmit }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState(null);
  const [category, setCategory] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dueTime, setDueTime] = useState(null);
  const [estimatedDuration, setEstimatedDuration] = useState("");
  const [dependencies, setDependencies] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [checklistItems, setChecklistItems] = useState([]);

  // Error State
  const [errors, setErrors] = useState({});

  // Helper to update value and clear error simultaneously
  const handleFieldChange = (field, value, setter) => {
    setter(value);
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};

    // -----------------------
    // REQUIRED VALIDATIONS
    // -----------------------
    if (!title.trim()) newErrors.title = "Task title is required";
    if (!selectedDate) newErrors.selectedDate = "Due date is required";
    if (!dueTime) newErrors.dueTime = "Due time is required";

    setErrors(newErrors);

    // Stop if validation fails
    if (Object.keys(newErrors).length > 0) {
      // Optional: Scroll to top of form to show errors
      passRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    const formData = {
      title,
      description,
      priority_id: priority,
      category_id: category,
      due_date: selectedDate,
      due_time: dueTime,
      estimated_duration: estimatedDuration,
      dependencies,
      additional_notes: additionalNotes,
      checklist: checklistItems,
    };

    handleSubmit(formData);
  };

  return (
    <form ref={passRef} onSubmit={onSubmit} className="flex flex-col gap-6">
      <div className="border border-border bg-card rounded-2xl items-start p-6">
        <div className="flex flex-col gap-1.5 mb-6">
          <div className="text-foreground text-2xl font-semibold leading-6">Task Details</div>
          <div className="text-muted-foreground text-sm font-normal leading-5">
            Basic information about your task
          </div>
        </div>

        <div className="flex flex-col items-start gap-4 w-full">
          {/* TASK TITLE (REQUIRED) */}
          <div className="flex flex-col items-start gap-2 w-full">
            <Label className="text-foreground text-sm font-bold leading-4" htmlFor="task_title">
              Task Title <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              name="task_title"
              id="task_title"
              placeholder="e.g., Setup lighting for main stage"
              onChange={(e) => handleFieldChange("title", e.target.value, setTitle)}
              value={title}
              error={!!errors.title}
            />
            {errors.title && <p className="text-red-500 text-xs">{errors.title}</p>}
          </div>

          {/* DESCRIPTION */}
          <div className="flex flex-col gap-2 w-full">
            <Label className="text-foreground text-sm font-bold leading-4" htmlFor="description">
              Description
            </Label>
            <Textarea
              name="description"
              id="description"
              placeholder="Provide detailed instructions for this task..."
              onChange={(e) => setDescription(e.target.value)}
              value={description}
            />
          </div>

          {/* PRIORITY + CATEGORY */}
          <div className="flex gap-4 w-full">
            <div className="w-full flex flex-col gap-2">
              <Label className="text-foreground text-sm font-bold leading-4">Priority Level</Label>
              <Select id="priority" options={priorityOptions} placeholder="Select priority" onChange={setPriority} />
            </div>

            <div className="w-full flex flex-col gap-2">
              <Label className="text-foreground text-sm font-bold leading-4">Category</Label>
              <Select id="category" options={categoryOptions} placeholder="Select Category" onChange={setCategory} />
            </div>
          </div>

          {/* DATE + TIME (REQUIRED) */}
          <div className="flex gap-4 w-full">
            <div className="w-full flex flex-col gap-2">
              <Label className="text-foreground text-sm font-bold leading-4">
                Due Date <span className="text-red-500">*</span>
              </Label>
              <DatePicker
                id="due_date"
                onChange={(d) => handleFieldChange("selectedDate", d, setSelectedDate)}
                error={!!errors.selectedDate}
              />
              {errors.selectedDate && <p className="text-red-500 text-xs">{errors.selectedDate}</p>}
            </div>

            <div className="w-full flex flex-col gap-2">
              <Label className="text-foreground text-sm font-bold leading-4">
                Due Time <span className="text-red-500">*</span>
              </Label>
              <TimePicker
                setTime={(t) => handleFieldChange("dueTime", t, setDueTime)}
                error={!!errors.dueTime}
              />
              {errors.dueTime && <p className="text-red-500 text-xs">{errors.dueTime}</p>}
            </div>
          </div>

          {/* ESTIMATED DURATION */}
          <div className="flex flex-col items-start gap-2 w-full">
            <Label className="text-foreground text-sm font-bold leading-4" htmlFor="estimated_duration">
              Estimated Duration (hours)
            </Label>
            <Input
              type="number"
              name="estimated_duration"
              id="estimated_duration"
              placeholder="2 hours"
              min={1}
              onChange={(e) => setEstimatedDuration(e.target.value)}
              value={estimatedDuration}
            />
          </div>

          {/* DEPENDENCIES */}
          <div className="flex flex-col gap-2 w-full">
            <Label className="text-foreground text-sm font-bold leading-4" htmlFor="dependencies">
              Dependencies (Optional)
            </Label>
            <Textarea
              name="dependencies"
              id="dependencies"
              placeholder="List any tasks that must be completed first..."
              value={dependencies.join("\n")}
              onChange={(e) => setDependencies(e.target.value.split("\n"))}
            />
          </div>

          {/* ADDITIONAL NOTES */}
          <div className="flex flex-col gap-2 w-full">
            <Label className="text-foreground text-sm font-bold leading-4" htmlFor="additional_notes">
              Additional Notes
            </Label>
            <Textarea
              name="additional_notes"
              id="additional_notes"
              placeholder="Any special instructions or important information..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* CHECKLIST ITEMS SECTION */}
      <div className="border border-border bg-card rounded-2xl items-start p-6">
        <div className="flex flex-col gap-1.5 mb-6">
          <div className="text-foreground text-2xl font-semibold leading-6">Checklist Items (Optional)</div>
          <div className="text-muted-foreground text-sm leading-5">Add optional checklist items for this task</div>
        </div>

        <AddItems
          placeholder={"Add checklist item..."}
          value={checklistItems}
          onChange={setChecklistItems}
        />
      </div>
    </form>
  );
};

export default TaskForm;
